// src/pages/Users/Inscription/components/FinalSummaryPanel.jsx

import React from "react";
import {
  FaCheckCircle,
  FaEuroSign,
  FaLayerGroup,
  FaPaperPlane,
  FaSpinner,
} from "react-icons/fa";

import StepCard from "./StepCard";

import {
  formatEuro,
  getGroupAmount,
  getHighestGroupAmount,
  getHighestPricedGroup,
} from "../utils/inscriptionUtils";

export default function FinalSummaryPanel({
  selectedGroups = [],
  canSubmit,
  submitting,
}) {
  const highestPricedGroup = getHighestPricedGroup(selectedGroups);
  const retainedAmount = getHighestGroupAmount(selectedGroups);

  return (
    <StepCard
      step="6"
      title="Finalisation"
      icon={<FaCheckCircle />}
      description="Vérifiez le récapitulatif puis validez l’inscription."
      right={
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
          <p className="text-xs font-bold uppercase tracking-[0.18em]">
            Tarif retenu
          </p>

          <p className="mt-1 text-2xl font-bold">
            {formatEuro(retainedAmount)}
          </p>

          {selectedGroups.length > 1 && highestPricedGroup && (
            <p className="mt-1 max-w-[220px] text-xs leading-5 opacity-80">
              Tarif le plus élevé parmi les groupes sélectionnés.
            </p>
          )}
        </div>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        {/* Groupes sélectionnés */}
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950/70">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              <FaLayerGroup />
            </span>

            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                Groupe(s) sélectionné(s)
              </p>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {selectedGroups.length} groupe(s) dans le dossier
              </p>
            </div>
          </div>

          {selectedGroups.length > 1 && (
            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-3 text-xs leading-5 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
              L’adhérent peut participer à plusieurs groupes sans cumuler les
              tarifs. Seul le tarif le plus élevé est retenu.
            </div>
          )}

          {selectedGroups.length ? (
            <div className="mt-4 space-y-2">
              {selectedGroups.map((group) => {
                const groupAmount = getGroupAmount(group);

                const isRetained =
                  highestPricedGroup &&
                  String(group.id) === String(highestPricedGroup.id);

                return (
                  <div
                    key={group.id}
                    className={`flex flex-col gap-2 rounded-2xl bg-white p-3 ring-1 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between ${
                      isRetained
                        ? "ring-emerald-300 dark:ring-emerald-700"
                        : "ring-slate-200 dark:ring-slate-800"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold text-slate-900 dark:text-white">
                          {group.nom}
                        </p>

                        {isRetained && (
                          <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                            Tarif retenu
                          </span>
                        )}
                      </div>

                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {group.discipline_groupe ||
                          "Discipline non renseignée"}
                      </p>

                      <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                        Licence : {formatEuro(group.tarif_licence)} ·
                        Cotisation : {formatEuro(group.tarif_cotisation)}
                      </p>
                    </div>

                    <p
                      className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ${
                        isRetained
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      }`}
                    >
                      <FaEuroSign />
                      {formatEuro(groupAmount)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              Aucun groupe sélectionné.
            </p>
          )}
        </div>

        {/* Validation */}
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            Prêt à valider ?
          </p>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Après validation, l’adhérent sera créé, rattaché à votre compte et
            un dossier d’inscription sera généré.
          </p>

          <div className="mt-4 rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/70">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Montant de l’inscription
            </p>

            <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {formatEuro(retainedAmount)}
            </p>

            {highestPricedGroup && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Tarif du groupe « {highestPricedGroup.nom} »
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-4 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            {submitting ? (
              <>
                <FaSpinner className="animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <FaPaperPlane />
                Valider l’inscription
              </>
            )}
          </button>

          {!canSubmit ? (
            <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Complétez les champs obligatoires, sélectionnez au moins un groupe
              et cochez les validations nécessaires.
            </p>
          ) : (
            <p className="mt-3 text-xs leading-5 text-emerald-600 dark:text-emerald-300">
              Le dossier semble complet. Vous pouvez finaliser l’inscription.
            </p>
          )}
        </div>
      </div>
    </StepCard>
  );
}